import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  api,
  authHeaders,
  fmtError,
  isLoggedIn,
  logout,
  useSiteData,
} from "@/lib/api";
import { toast } from "sonner";
import { RED, Field, Input, Textarea, PrimaryBtn, SecondaryBtn, ImageUploadField } from "@/components/admin/ui";
import { ContentTab } from "@/components/admin/ContentTab";
import { ImagesTab } from "@/components/admin/ImagesTab";
import { InboxTab } from "@/components/admin/InboxTab";
import { AutofillBar } from "@/components/admin/AutofillBar";
import { ResumeUploadButton } from "@/components/admin/ResumeUploadButton";
import {
  Plus,
  Trash2,
  Save,
  LogOut,
  ExternalLink,
  Pencil,
  RefreshCw,
  X,
} from "lucide-react";


const TABS = [
  { id: "projects", label: "Projects" },
  { id: "social", label: "Social" },
  { id: "articles", label: "Articles" },
  { id: "content", label: "Site text" },
  { id: "images", label: "Images" },
  { id: "inbox", label: "Inbox" },
];

export default function Admin() {
  const nav = useNavigate();
  const { projects, social, articles, content, loading, reload } = useSiteData();
  const [tab, setTab] = useState("projects");

  useEffect(() => {
    if (!isLoggedIn()) nav("/admin/login");
    else axios.get(api("/api/auth/me"), { headers: authHeaders() }).catch(() => {});
  }, [nav]);

  const doLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-white text-black" style={{ fontFamily: '"Manrope", system-ui, sans-serif' }}>
      <header className="border-b border-black/15 px-6 md:px-10 py-4 flex items-center justify-between gap-3 md:sticky top-0 bg-white z-30">
        <div className="flex items-center gap-3">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: RED }} />
          <h1 className="font-black text-xl" style={{ fontFamily: '"Bricolage Grotesque", system-ui' }}>
            Portfolio Admin
          </h1>
          <span className="text-xs text-black/50 hidden md:inline">{localStorage.getItem("admin_email")}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <ResumeUploadButton content={content} reload={reload} />
          <a href="/" target="_blank" rel="noreferrer" className="text-xs font-bold uppercase tracking-wider px-3 py-2 border border-black/20 rounded-full hover:bg-black hover:text-white transition-colors">
            View site ↗
          </a>
          <button onClick={reload} data-testid="admin-reload" className="text-xs font-bold uppercase tracking-wider px-3 py-2 border border-black/20 rounded-full hover:bg-black hover:text-white transition-colors flex items-center gap-1">
            <RefreshCw size={12} /> Reload
          </button>
          <button onClick={doLogout} data-testid="admin-logout" className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full text-white flex items-center gap-1" style={{ background: RED }}>
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      <nav className="border-b border-black/10 px-6 md:px-10 sticky top-0 md:top-[57px] bg-white z-20">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 font-bold text-sm uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id ? "border-black text-black" : "border-transparent text-black/50 hover:text-black"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="px-6 md:px-10 py-8 max-w-6xl mx-auto">
        {loading && <p className="text-sm text-black/50">Loading…</p>}
        {!loading && tab === "projects" && <ProjectsTab items={projects} reload={reload} />}
        {!loading && tab === "social" && <SocialTab items={social} reload={reload} />}
        {!loading && tab === "articles" && <ArticlesTab items={articles} reload={reload} />}
        {!loading && tab === "content" && <ContentTab content={content} reload={reload} />}
        {!loading && tab === "inbox" && <InboxTab />}
        {!loading && tab === "images" && <ImagesTab content={content} projects={projects} social={social} reload={reload} />}
      </main>
    </div>
  );
}

/* ------------------------------ Projects tab ------------------------------ */

const EMPTY_PROJECT = {
  tag: "",
  title: "",
  subtitle: "",
  body: "",
  team: false,
  href: "",
  img: "",
  role: "",
  color: "#4A0E17",
  bucket: "campaigns",
  order: 99,
};

function ProjectsTab({ items, reload }) {
  const [editing, setEditing] = useState(null);

  const startNew = () => setEditing({ ...EMPTY_PROJECT });
  const startEdit = (it) => setEditing({ ...it });

  const save = async (item) => {
    if (!item.title || !item.title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      if (item.id) {
        await axios.put(api(`/api/admin/projects/${item.id}`), item, { headers: authHeaders() });
      } else {
        await axios.post(api("/api/admin/projects"), item, { headers: authHeaders() });
      }
      toast.success("Saved");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await axios.delete(api(`/api/admin/projects/${id}`), { headers: authHeaders() });
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  return (
    <div data-testid="tab-projects">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="text-3xl font-black" style={{ fontFamily: '"Bricolage Grotesque", system-ui' }}>Projects</h2>
          <p className="text-sm text-black/60">{items.length} item(s) · lowest order shows first.</p>
        </div>
        <PrimaryBtn onClick={startNew} data-testid="project-add">
          <Plus size={14} /> New project
        </PrimaryBtn>
      </div>

      <div className="grid grid-cols-1 gap-3 min-w-0">
        {items.map((p) => (
          <div key={p.id} data-testid={`project-row-${p.id}`} className="border border-black/10 p-3 flex items-center gap-2 md:gap-4 min-w-0">
            <img src={p.img} alt="" className="w-14 h-14 md:w-20 md:h-20 object-cover border border-black/10 shrink-0" />
            <span data-testid={`project-color-swatch-${p.id}`} className="w-5 h-5 rounded-full border border-black/10 shrink-0" style={{ background: p.color || "#4A0E17" }} />
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{p.title || "(untitled)"}</p>
              <p className="text-xs text-black/60 truncate">
                {p.tag} · {p.subtitle} {p.team ? "· Team" : ""} · #{p.order}
              </p>
            </div>
            {p.href && (
              <a href={p.href} target="_blank" rel="noreferrer" className="p-2 text-black/60 hover:text-black"><ExternalLink size={16} /></a>
            )}
            <button onClick={() => startEdit(p)} data-testid={`project-edit-${p.id}`} className="p-2 text-black/60 hover:text-black"><Pencil size={16} /></button>
            <button onClick={() => remove(p.id)} data-testid={`project-delete-${p.id}`} className="p-2" style={{ color: RED }}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit project" : "New project"}>
          <ProjectForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
}

function ProjectForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-4">
      <AutofillBar kind="project" form={form} setForm={setForm} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Tag (e.g. Adobe '25)"><Input value={form.tag} onChange={set("tag")} /></Field>
        <Field label="Bucket (campaigns / analyses / iris)">
          <select value={form.bucket} onChange={set("bucket")} className="w-full border-2 border-black/15 focus:border-black px-3 py-2 outline-none">
            <option value="campaigns">campaigns</option>
            <option value="analyses">analyses</option>
            <option value="iris">iris</option>
            <option value="other">other</option>
          </select>
        </Field>
      </div>
      <Field label="Title"><Input value={form.title} onChange={set("title")} /></Field>
      <Field label="Subtitle"><Input value={form.subtitle} onChange={set("subtitle")} /></Field>
      <Field label="Body (description)"><Textarea rows={3} value={form.body} onChange={set("body")} /></Field>
      <Field label="Your role (shown on playful version)"><Input data-testid="project-role" value={form.role} onChange={set("role")} /></Field>
      <Field label="Link (Google Drive deck, Notion, PDF, etc.)"><Input data-testid="project-href" value={form.href} onChange={set("href")} placeholder="https://…" /></Field>
      <Field label="Brand colour (boarding pass)">
        <div className="flex items-center gap-3">
          <input type="color" data-testid="project-color-picker" value={form.color || "#4A0E17"} onChange={set("color")} className="w-12 h-10 border-2 border-black/15 cursor-pointer" />
          <Input data-testid="project-color-input" value={form.color || ""} onChange={set("color")} placeholder="#EB1000" />
        </div>
      </Field>
      <Field label="Image"><ImageUploadField value={form.img} onChange={(v) => setForm({ ...form, img: v })} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Display order (smaller = earlier)">
          <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
        </Field>
        <Field label="Team project?">
          <label className="flex items-center gap-2 mt-1">
            <input type="checkbox" checked={!!form.team} onChange={(e) => setForm({ ...form, team: e.target.checked })} />
            <span className="text-sm">Show "Team" badge</span>
          </label>
        </Field>
      </div>
      <div className="flex gap-3 pt-2">
        <PrimaryBtn data-testid="project-save" onClick={() => onSave(form)}><Save size={14} /> Save</PrimaryBtn>
        <SecondaryBtn onClick={onCancel}><X size={14} /> Cancel</SecondaryBtn>
      </div>
    </div>
  );
}

/* ------------------------------- Social tab ------------------------------- */

const EMPTY_SOCIAL = { client: "", role: "", series: "", body: "", href: "", img: "", order: 99 };

function SocialTab({ items, reload }) {
  const [editing, setEditing] = useState(null);
  const save = async (item) => {
    try {
      if (item.id) {
        await axios.put(api(`/api/admin/social/${item.id}`), item, { headers: authHeaders() });
      } else {
        await axios.post(api("/api/admin/social"), item, { headers: authHeaders() });
      }
      toast.success("Saved");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await axios.delete(api(`/api/admin/social/${id}`), { headers: authHeaders() });
    toast.success("Deleted");
    reload();
  };

  return (
    <div data-testid="tab-social">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="text-3xl font-black" style={{ fontFamily: '"Bricolage Grotesque", system-ui' }}>Social &amp; personal brand</h2>
          <p className="text-sm text-black/60">{items.length} reel(s)</p>
        </div>
        <PrimaryBtn onClick={() => setEditing({ ...EMPTY_SOCIAL })} data-testid="social-add">
          <Plus size={14} /> New reel
        </PrimaryBtn>
      </div>

      <div className="grid grid-cols-1 gap-3 min-w-0">
        {items.map((s) => (
          <div key={s.id} data-testid={`social-row-${s.id}`} className="border border-black/10 p-3 flex items-center gap-2 md:gap-4 min-w-0">
            <img src={s.img} alt="" className="w-16 h-24 object-cover border border-black/10 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{s.client} · {s.series}</p>
              <p className="text-xs text-black/60 truncate">{s.role} · #{s.order}</p>
            </div>
            {s.href && (
              <a href={s.href} target="_blank" rel="noreferrer" className="p-2 text-black/60"><ExternalLink size={16} /></a>
            )}
            <button onClick={() => setEditing({ ...s })} className="p-2 text-black/60"><Pencil size={16} /></button>
            <button onClick={() => remove(s.id)} className="p-2" style={{ color: RED }}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit reel" : "New reel"}>
          <SocialForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
}

function SocialForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-4">
      <AutofillBar kind="reel" form={form} setForm={setForm} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Client name"><Input data-testid="social-client" value={form.client} onChange={set("client")} placeholder="Gagan Aggarwal" /></Field>
        <Field label="Role (e.g. Hybrid Athlete)"><Input data-testid="social-role" value={form.role} onChange={set("role")} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Series label (Series I, II, …)"><Input data-testid="social-series" value={form.series} onChange={set("series")} /></Field>
        <Field label="View count (e.g. 12.4K)"><Input data-testid="social-views" value={form.views || ""} onChange={set("views")} /></Field>
      </div>
      <Field label="Body"><Textarea data-testid="social-body" rows={3} value={form.body} onChange={set("body")} /></Field>
      <Field label="Link to reel / video"><Input data-testid="social-href" value={form.href} onChange={set("href")} placeholder="https://…" /></Field>
      <Field label="Thumbnail image"><ImageUploadField value={form.img} onChange={(v) => setForm({ ...form, img: v })} /></Field>
      <Field label="Display order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></Field>
      <div className="flex gap-3 pt-2">
        <PrimaryBtn onClick={() => onSave(form)} data-testid="social-save"><Save size={14} /> Save</PrimaryBtn>
        <SecondaryBtn onClick={onCancel}><X size={14} /> Cancel</SecondaryBtn>
      </div>
    </div>
  );
}

/* ------------------------------ Articles tab ------------------------------ */

const EMPTY_ARTICLE = { date: "", title: "", blurb: "", href: "", order: 99 };

function ArticlesTab({ items, reload }) {
  const [editing, setEditing] = useState(null);
  const save = async (item) => {
    if (!item.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      if (item.id) {
        await axios.put(api(`/api/admin/articles/${item.id}`), item, { headers: authHeaders() });
      } else {
        await axios.post(api("/api/admin/articles"), item, { headers: authHeaders() });
      }
      toast.success("Saved");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await axios.delete(api(`/api/admin/articles/${id}`), { headers: authHeaders() });
    toast.success("Deleted");
    reload();
  };

  return (
    <div data-testid="tab-articles">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="text-3xl font-black" style={{ fontFamily: '"Bricolage Grotesque", system-ui' }}>Articles</h2>
          <p className="text-sm text-black/60">{items.length} article(s)</p>
        </div>
        <PrimaryBtn onClick={() => setEditing({ ...EMPTY_ARTICLE })} data-testid="article-add">
          <Plus size={14} /> New article
        </PrimaryBtn>
      </div>

      <div className="grid grid-cols-1 gap-3 min-w-0">
        {items.map((a) => (
          <div key={a.id} data-testid={`article-row-${a.id}`} className="border border-black/10 p-3 flex items-center gap-2 md:gap-4 min-w-0">
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{a.title}</p>
              <p className="text-xs text-black/60 truncate">{a.date} · {a.blurb} · #{a.order}</p>
            </div>
            {a.href && (
              <a href={a.href} target="_blank" rel="noreferrer" className="p-2 text-black/60"><ExternalLink size={16} /></a>
            )}
            <button onClick={() => setEditing({ ...a })} className="p-2 text-black/60"><Pencil size={16} /></button>
            <button onClick={() => remove(a.id)} className="p-2" style={{ color: RED }}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit article" : "New article"}>
          <ArticleForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
}

function ArticleForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-4">
      <AutofillBar kind="article" form={form} setForm={setForm} />
      <Field label="Title"><Input value={form.title} onChange={set("title")} /></Field>
      <Field label="Date (e.g. Apr 11, 2025)"><Input value={form.date} onChange={set("date")} /></Field>
      <Field label="Blurb / subtitle"><Textarea rows={2} value={form.blurb} onChange={set("blurb")} /></Field>
      <Field label="Link (Medium / LinkedIn URL)"><Input value={form.href} onChange={set("href")} /></Field>
      <Field label="Display order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></Field>
      <div className="flex gap-3 pt-2">
        <PrimaryBtn onClick={() => onSave(form)} data-testid="article-save"><Save size={14} /> Save</PrimaryBtn>
        <SecondaryBtn onClick={onCancel}><X size={14} /> Cancel</SecondaryBtn>
      </div>
    </div>
  );
}

/* --------------------------------- Modal --------------------------------- */

function Modal({ children, title, onClose }) {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-2xl border-2 border-black p-6 md:p-8 my-8">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-2xl font-black" style={{ fontFamily: '"Bricolage Grotesque", system-ui' }}>{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-full" data-testid="modal-close">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
